import React, { useState, useEffect } from 'react';
import type { ApiKeySettingsProps, SettingsSection } from './types';
import { useSettingsMessage } from './hooks/useSettingsMessage';
import { useApiKeyManagement } from './hooks/useApiKeyManagement';
import { useApplicationSettings } from './hooks/useApplicationSettings';
import { useCacheManagement } from './hooks/useCacheManagement';
import { MessageBanner } from './components/MessageBanner';
import { SettingsOverview } from './components/SettingsOverview';
import { SectionTabs } from './components/SectionTabs';
import { ApiKeysSection } from './components/ApiKeysSection';
import { ApplicationSection } from './components/ApplicationSection';
import { AdditionalSection } from './components/AdditionalSection';

export const ApiKeySettings: React.FC<ApiKeySettingsProps> = ({ onApiKeyChange }) => {
  const [activeSection, setActiveSection] = useState<SettingsSection>('apiKeys');

  const { message, showMessage } = useSettingsMessage();
  const { isLoading, keyInfo, checkStoredKeys, openaiAdmin, openaiStandard, anthropic } =
    useApiKeyManagement(showMessage, onApiKeyChange);
  const { devSettings, showHolidays, notificationThreshold, handleDevSettingsChange, handleResetDevSettings, handleToggleHolidays, handleThresholdChange } =
    useApplicationSettings(showMessage);
  const { cacheStats, migrationStatus, isMigrating, loadCacheStats, loadMigrationStatus, handleMigration, handleClearExpired, handleClearAll, handleClearServiceCache, formatBytes } =
    useCacheManagement(showMessage);

  useEffect(() => {
    checkStoredKeys();
    loadCacheStats();
    loadMigrationStatus();
  }, [checkStoredKeys, loadCacheStats, loadMigrationStatus]);

  const configuredProvidersCount = [openaiAdmin.hasStoredKey, openaiStandard.hasStoredKey, anthropic.hasStoredKey].filter(Boolean).length;

  return (
    <div className="w-full max-w-7xl mx-auto px-1.5 sm:px-3 pb-3 sm:pb-4">
      <MessageBanner message={message} />

      <SettingsOverview
        configuredProvidersCount={configuredProvidersCount}
        hasOpenAIAdmin={openaiAdmin.hasStoredKey}
        hasAnthropic={anthropic.hasStoredKey}
        keyCreatedAt={keyInfo?.createdAt}
      />

      <SectionTabs activeSection={activeSection} onSectionChange={setActiveSection} />

      <ApiKeysSection
        isActive={activeSection === 'apiKeys'}
        isLoading={isLoading}
        openaiAdmin={openaiAdmin}
        openaiStandard={openaiStandard}
        anthropic={anthropic}
      />

      <ApplicationSection
        isActive={activeSection === 'application'}
        devSettings={devSettings}
        notificationThreshold={notificationThreshold}
        onDevSettingsChange={handleDevSettingsChange}
        onResetDevSettings={handleResetDevSettings}
        onThresholdChange={handleThresholdChange}
        cacheStats={cacheStats}
        migrationStatus={migrationStatus}
        isMigrating={isMigrating}
        onMigration={handleMigration}
        onClearExpired={handleClearExpired}
        onClearAll={handleClearAll}
        onClearServiceCache={handleClearServiceCache}
        onRefreshStats={loadCacheStats}
        formatBytes={formatBytes}
      />

      <AdditionalSection
        isActive={activeSection === 'additional'}
        showHolidays={showHolidays}
        onToggleHolidays={handleToggleHolidays}
        onShowMessage={showMessage}
      />
    </div>
  );
};
